import { Resolver } from 'react-hook-form';
import dayjs from 'dayjs';

import { PAYROLL_MODEL_IDS, PayrollModelId } from '@xemplo/gp-types';

import { getMilestoneFields, MILESTONE_FIELDS } from '../edit-payrun-milestones.const';
import { combineDateTime } from '../edit-payrun-milestones.helpers';
import {
  MilestoneFieldConfig,
  MilestoneFieldKey,
  MilestoneFormValues,
} from '../edit-payrun-milestones.types';

const WEEKEND_DAYS = [0, 6]; // Sunday, Saturday

export const SEQUENTIAL_ERROR = {
  type: 'sequential',
  message: 'Dates must be sequential',
} as const;

export const REQUIRED_DATE_ERROR = {
  type: 'required',
  message: 'Please select a date',
} as const;

export const REQUIRED_TIME_ERROR = {
  type: 'required',
  message: 'Please select a time',
} as const;

type MilestoneErrors = Record<string, { type: string; message: string }>;

/** Milestones that must fall in chronological order. Pay cycle dates are
 * excluded — a cycle can end after pay date (advance-paid runs). */
export const SEQUENTIAL_CHAIN: MilestoneFieldKey[] = [
  'amendmentDue',
  'preparationDue',
  'approvalDue',
  'finaliseDue',
  'paymentDue',
  'payDateTime',
];

/** First milestone whose date is not strictly after the previous one, or null. */
export function getSequentialViolation(
  values: MilestoneFormValues,
  chain: MilestoneFieldKey[] = SEQUENTIAL_CHAIN,
): MilestoneFieldKey | null {
  let previous: Date | null = null;
  for (const key of chain) {
    const date = combineDateTime(values[key]);
    if (!date) continue;
    if (previous && !dayjs(previous).isBefore(date)) return key;
    previous = date;
  }
  return null;
}

/**
 * Labels of milestones that land on a weekend. Non-blocking — surfaced as a
 * warning, never gates submission, so it lives outside the resolver.
 */
export function getWeekendMilestones(
  values: MilestoneFormValues,
  fields: MilestoneFieldConfig[] = MILESTONE_FIELDS,
): string[] {
  return fields
    .filter((field) => {
      const date = combineDateTime(values[field.key]);
      return date !== null && WEEKEND_DAYS.includes(dayjs(date).day());
    })
    .map((field) => field.label);
}

function getRequiredErrors(
  values: MilestoneFormValues,
  fields: MilestoneFieldConfig[],
): MilestoneErrors {
  const errors: MilestoneErrors = {};
  for (const { key } of fields) {
    const { date, time } = values[key];
    if (!date) errors[`${key}.date`] = REQUIRED_DATE_ERROR;
    if (!time) errors[`${key}.time`] = REQUIRED_TIME_ERROR;
  }
  return errors;
}

/**
 * RHF resolver: runs on submit, then on every change once submitted. Blocks
 * submission and surfaces errors automatically — no manual setError needed.
 *
 * Required errors are attached to each field's own path (`payDate.date`) so the
 * inline message renders on the input. The sequential error shares the date
 * path but yields to a required error already there — an empty date is skipped
 * by the sequential check, so the two never collide on the same field.
 */
export function createMilestoneResolver(
  payrollModelId: PayrollModelId,
): Resolver<MilestoneFormValues> {
  const fields = getMilestoneFields(payrollModelId);
  const chain = SEQUENTIAL_CHAIN.filter((key) =>
    fields.some((field) => field.key === key),
  );

  return (values) => {
    const errors = getRequiredErrors(values, fields);

    const violation = getSequentialViolation(values, chain);
    if (violation && !errors[`${violation}.date`]) {
      errors[`${violation}.date`] = {
        type: SEQUENTIAL_ERROR.type,
        message: SEQUENTIAL_ERROR.message,
      };
    }

    if (Object.keys(errors).length > 0) return { values: {}, errors };
    return { values, errors: {} };
  };
}

/** Default resolver covering every milestone (PEO). */
export const milestoneResolver = createMilestoneResolver(PAYROLL_MODEL_IDS.PEO);
